import { useEffect, useMemo, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { scrollTo } from "@/components/Nav";

const EASE = [0.16, 1, 0.3, 1];

// Positions are taken from the master logo so the letters keep its spacing (N and A overlap slightly).
const LETTERS = [
  { src: "/assets/dna-d.png?v=6", name: "d", left: "0%", width: "30.93%", delay: 2.4 },
  { src: "/assets/dna-n.png?v=6", name: "n", left: "32.97%", width: "31.35%", delay: 2.9 },
  { src: "/assets/dna-a.png?v=6", name: "a", left: "63.82%", width: "36.18%", delay: 3.4 },
];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <div className="overflow-hidden">
    <motion.div
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.div>
  </div>
);

const Hero = () => {
  const { scrollY } = useScroll();
  const yText = useTransform(scrollY, [0, 800], [0, 120]);
  const yLogo = useTransform(scrollY, [0, 800], [0, -60]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);
  const [curtainGone, setCurtainGone] = useState(false);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 60, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 60, damping: 16 });

  useEffect(() => {
    const t = setTimeout(() => setCurtainGone(true), 9200);
    return () => clearTimeout(t);
  }, []);

  const particles = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        tx: 6 + Math.random() * 34,
        ty: 28 + Math.random() * 28,
        size: 2 + Math.random() * 3,
        delay: 0.3 + Math.random() * 1.2,
      })),
    []
  );

  const handleMouse = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      onMouseMove={handleMouse}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-[72px]"
      data-testid="hero-section"
    >
      {!curtainGone && (
        <>
          <motion.div
            className="fixed inset-0 z-[55] bg-[#070707] pointer-events-none"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ delay: 6.6, duration: 2, ease: "easeInOut" }}
            data-testid="hero-curtain"
          />
          <motion.div
            className="fixed inset-0 z-[57] pointer-events-none"
            style={{ background: "linear-gradient(105deg, transparent 42%, rgba(217,160,107,0.16) 50%, transparent 58%)" }}
            initial={{ x: "-70%" }}
            animate={{ x: "70%" }}
            transition={{ delay: 1.4, duration: 5.5, ease: "easeInOut" }}
          />
          <motion.div
            className="fixed inset-0 z-[58] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at 30% 45%, rgba(217,160,107,0.6) 0%, transparent 55%)" }}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: [0, 0.85, 0.85, 0], scale: [0.85, 1.1, 1.18, 1.25] }}
            transition={{ delay: 1.2, duration: 6.8, ease: "easeInOut", times: [0, 0.45, 0.72, 1] }}
          />
          {particles.map((p) => (
            <motion.span
              key={p.id}
              className="fixed rounded-full z-[60] pointer-events-none"
              style={{
                width: p.size,
                height: p.size,
                background: "#D9A06B",
                boxShadow: "0 0 8px rgba(217,160,107,0.9)",
              }}
              initial={{ left: `${p.x}vw`, top: `${p.y}vh`, opacity: 0 }}
              animate={{ left: `${p.tx}vw`, top: `${p.ty}vh`, opacity: [0, 1, 1, 0] }}
              transition={{ duration: 2.4, delay: p.delay, ease: "easeInOut", times: [0, 0.2, 0.8, 1] }}
            />
          ))}
        </>
      )}

      <div className="absolute top-24 right-6 md:right-12 hidden lg:block z-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 8.6, duration: 1 }}
          className="text-[10px] tracking-[0.4em] uppercase text-[#5C5C5C] [writing-mode:vertical-rl]"
        >
          Est. 2026 — The Triple Threat Formula
        </motion.p>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-12 w-full relative z-[60]">
        <motion.div
          style={{ y: yLogo, rotateX, rotateY, transformPerspective: 900 }}
          className="relative inline-block"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2.2, delay: 4 }}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[190%] rounded-full pointer-events-none"
          >
            <motion.div
              animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.06, 1] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="w-full h-full rounded-full"
              style={{ background: "radial-gradient(ellipse, rgba(192,124,95,0.13) 0%, transparent 65%)" }}
            />
          </motion.div>

          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative"
          >
            <div className="relative w-[84vw] max-w-[620px] aspect-[7755/2159]" data-testid="hero-logo">
              {LETTERS.map((l) => (
                <motion.div
                  key={l.name}
                  className="absolute top-0 h-full"
                  style={{ left: l.left, width: l.width }}
                  initial={{ opacity: 0, y: -70, filter: "blur(16px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{ duration: 1.5, delay: l.delay, ease: EASE }}
                >
                  <img
                    src={l.src}
                    alt=""
                    data-testid={`hero-letter-${l.name}`}
                    className="w-full h-full block"
                    style={{ filter: "drop-shadow(0 0 22px rgba(192,124,95,0.35))" }}
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      WebkitMaskImage: `url(${l.src})`,
                      maskImage: `url(${l.src})`,
                      WebkitMaskSize: "100% 100%",
                      maskSize: "100% 100%",
                      WebkitMaskRepeat: "no-repeat",
                      maskRepeat: "no-repeat",
                    }}
                  >
                    <motion.div
                      initial={{ x: "-130%" }}
                      animate={{ x: "130%" }}
                      transition={{ duration: 1.4, delay: l.delay + 0.8, ease: "easeInOut", repeat: Infinity, repeatDelay: 6.5 }}
                      className="w-1/2 h-full"
                      style={{ background: "linear-gradient(100deg, transparent 15%, rgba(255,255,255,0.55) 50%, transparent 85%)" }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0, letterSpacing: "0.9em" }}
              animate={{ opacity: 1, letterSpacing: "0.42em" }}
              transition={{ delay: 4.8, duration: 2, ease: EASE }}
              className="mt-6 md:mt-8 text-sm md:text-lg font-medium uppercase text-[#C07C5F] whitespace-nowrap"
              data-testid="hero-name"
            >
              Daily Nutrition Aesthetics
            </motion.p>
          </motion.div>
        </motion.div>
      </div>

      <motion.div style={{ y: yText, opacity }} className="max-w-[1600px] mx-auto px-6 md:px-12 w-full relative z-10">
        <h1 className="font-serif-lux leading-[0.9] tracking-tighter text-[#111111] mt-10 md:mt-14">
          <MaskedLine delay={7.4} className="text-[8vw] md:text-[4.2vw]">
            <span className="italic font-semibold uppercase" data-testid="hero-tagline">To touch a billion lives.</span>
          </MaskedLine>
        </h1>

        <div className="mt-12 md:mt-16 flex flex-col md:flex-row md:items-end gap-10 md:gap-24">
          <MaskedLine delay={8}>
            <p className="max-w-md text-sm md:text-base font-bold text-[#111111] leading-relaxed" data-testid="hero-subcopy">
              Two formulations. One standard. Collagen engineered for cellular
              radiance; creatine calibrated for pure power. 10g of clean
              functional protein, architectural design, zero digestive bloat.
              <br />
              Nutrition re-engineered for your daily aesthetic.
            </p>
          </MaskedLine>
          <MaskedLine delay={8.2}>
            <div className="flex items-center">
              <button
                onClick={() => scrollTo("#collection")}
                data-testid="order-hero-btn"
                className="group flex items-center gap-3 bg-[#111111] text-[#F9F8F6] rounded-full px-8 py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500"
              >
                Order Now
                <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1.5" />
              </button>
            </div>
          </MaskedLine>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 8.8, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-12 flex items-center gap-3 text-[10px] tracking-[0.35em] uppercase text-[#5C5C5C] z-10"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity }}>
          <ArrowDown size={12} />
        </motion.span>
        Scroll
      </motion.div>
    </section>
  );
};

export default Hero;
